// file: /src/Pages/NewReservation/StepOne/algorithm/isDateAvailableWithTableCheck.js
const moment = require('moment-timezone');
const { isDateAvailable } = require('./isDateAvailable');
const { getAvailableTimeblocks } = require('./getAvailableTimeblocks');
const { isTimeAvailableSync } = require('./simulateTableAssignment');
const { daysOfWeekEnglish, getMealTypeByTime } = require('./restaurant_data/openinghours');

const TIMEZONE = 'Europe/Brussels';

/**
 * Checks if the specific meal/time also contains the giftcard. 
 * For instance, if Wednesday-lunch includes "Woensdag bon," 
 * but the user picks 19:00 (dinner), we should exclude that time.
 *
 * @param {Object} data - The restaurant data object.
 * @param {string} dateStr - "YYYY-MM-DD".
 * @param {string} timeStr - "HH:MM".
 * @param {string} giftcard - The selected giftcard.
 * @returns {boolean} true if the meal block for timeStr on dateStr includes the giftcard, else false.
 */
function timeHasGiftcard(data, dateStr, timeStr, giftcard) {
  if (!giftcard || !giftcard.trim()) return true; // No giftcard => no restriction

  // Figure out the meal type from timeStr
  const mealType = getMealTypeByTime(timeStr); // returns "breakfast", "lunch", or "dinner"
  if (!mealType) {
    return false; // Not in any recognized meal
  }

  // Day-of-week in Brussels TZ
  const m = moment.tz(dateStr, 'YYYY-MM-DD', TIMEZONE);
  if (!m.isValid()) return false;
  const englishDay = daysOfWeekEnglish[m.day()];

  const ohKey = `openinghours-${mealType}`;
  const daySettings =
    data[ohKey] &&
    data[ohKey].schemeSettings &&
    data[ohKey].schemeSettings[englishDay]
      ? data[ohKey].schemeSettings[englishDay]
      : null;

  if (!daySettings) {
    console.error(`No day settings found for ${englishDay} ${mealType}`);
    return false;
  }

  if (daySettings.giftcardsEnabled !== true) {
    return false;
  }
  
  if (!Array.isArray(daySettings.giftcards) || daySettings.giftcards.length === 0) {
    return false;
  }

  // Check if the selected giftcard is in the allowed list
  return daySettings.giftcards.includes(giftcard);
}

/**
 * Checks if a date is available for a reservation with a table assignment check.
 * This extends isDateAvailable by also verifying that at least one time slot
 * has an available table AND (if giftcard is selected) that the time belongs
 * to a meal that includes that giftcard. It also filters dates based on the
 * selected menu item's filterStartDate and filterEndDate from
 * window.currentReservationTicketItem, but only when normalOpeningTimes is false.
 *
 * @param {Object} data - The restaurant data object containing settings and table information.
 * @param {string} dateStr - The date string in "YYYY-MM-DD" format.
 * @param {Array} reservations - An array of reservation objects.
 * @param {number} guests - The number of guests for the reservation.
 * @param {string|null} selectedGiftcard - The selected giftcard (if any).
 * @returns {boolean} - Returns true if the date passes all checks to be available.
 */
function isDateAvailableWithTableCheck(data, dateStr, reservations, guests, selectedGiftcard) {
  // 0) Optionally filter by selected menu item's date range (only when normalOpeningTimes = false)
  const currentItem = typeof window !== 'undefined' ? window.currentReservationTicketItem : null;
  if (
    currentItem &&
    !currentItem.normalOpeningTimes
  ) {
    const dateM = moment.tz(dateStr, 'YYYY-MM-DD', TIMEZONE).startOf('day');
    const startM = currentItem.filterStartDate
      ? moment.tz(currentItem.filterStartDate, 'YYYY-MM-DD', TIMEZONE).startOf('day')
      : null;
    const endM = currentItem.filterEndDate
      ? moment.tz(currentItem.filterEndDate, 'YYYY-MM-DD', TIMEZONE).endOf('day')
      : null;

    if (startM && dateM.isBefore(startM)) {
      console.log(`Date ${dateStr} is before the allowed start ${currentItem.filterStartDate}`);
      return false;
    }
    if (endM && dateM.isAfter(endM)) {
      console.log(`Date ${dateStr} is after the allowed end ${currentItem.filterEndDate}`);
      return false;
    }
  }

  console.log(`\n===== Checking date availability for ${guests} guests on ${dateStr} =====`);
  console.log('Restaurant data floors:', data.floors ? 'present' : 'missing');
  console.log('Table settings:', JSON.stringify(data['table-settings'] || {}, null, 2));

  // Calculate table assignment flag once to reduce redundant checks
  const tableSettings = data?.['table-settings'] || {};
  const isTableAssignmentEnabled =
    tableSettings.isInstalled === true &&
    tableSettings.assignmentMode === 'automatic' &&
    data.floors &&
    Array.isArray(data.floors);

  console.log(`Table assignment is ${isTableAssignmentEnabled ? 'ENABLED' : 'DISABLED'} for date ${dateStr}`);

  // 1) First, do the standard day-level checks (including simple giftcard check).
  const basicDateAvailable = isDateAvailable(data, dateStr, reservations, guests, selectedGiftcard);
  if (!basicDateAvailable) {
    console.log(`Date ${dateStr} fails basic availability check`);
    return false;
  }

  // If table assignment is not enabled, stop here - the date is available
  if (!isTableAssignmentEnabled) {
    console.log(`Table assignment disabled, date ${dateStr} is available`);
    return true;
  }

  // 2) Get all available timeblocks for this date
  console.log(`Getting available timeblocks for ${dateStr}`);
  const availableTimeblocks = getAvailableTimeblocks(data, dateStr, reservations, guests);
  console.log(`Found ${Object.keys(availableTimeblocks).length} available timeblocks before table check`);

  // If no timeblocks are available at all, exit early
  if (Object.keys(availableTimeblocks).length === 0) {
    console.log(`No available timeblocks on ${dateStr}, date is unavailable`);
    return false;
  }

  // 3) Check if any timeblock has an available table and satisfies giftcard requirements
  console.log(`Checking table availability for each timeblock on ${dateStr}`);

  let atLeastOneAvailable = false;
  let checkedCount = 0;

  for (const time of Object.keys(availableTimeblocks)) {
    checkedCount++;

    // If there's a giftcard, confirm this particular time belongs to a meal that has that giftcard
    if (selectedGiftcard && !timeHasGiftcard(data, dateStr, time, selectedGiftcard)) {
      console.log(`Time ${time} doesn't match giftcard meal requirements`);
      continue;
    }

    console.log(`Checking table availability for ${time} on ${dateStr} for ${guests} guests`);
    if (isTimeAvailableSync(data, dateStr, time, guests, reservations)) {
      console.log(`Found available time ${time} with table assignment!`);
      atLeastOneAvailable = true;
      break;
    }
  }

  console.log(`Checked ${checkedCount}/${Object.keys(availableTimeblocks).length} timeblocks`);
  console.log(`Date ${dateStr} with table check: ${atLeastOneAvailable ? 'Available' : 'Unavailable'}`);
  
  return atLeastOneAvailable;
}

module.exports = {
  isDateAvailableWithTableCheck,
};
